import { useState, useCallback, useEffect } from "react";
import { useFaust } from "../../contexts/FaustContext";
import { useCompileOptions } from "../../hooks/useCompileOptions";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";

export function CompileOptions() {
    const { runDsp, editorRef, audioEnvRef } = useFaust();
    const { getOptions, setOption } = useCompileOptions();

    // Local state for controlled components
    const opts = getOptions();
    const [voices, setVoices] = useState(String(opts.voices));
    const [bufferSize, setBufferSize] = useState(String(opts.bufferSize));
    const [useDouble, setUseDouble] = useState(opts.useDouble);
    const [saveCode, setSaveCode] = useState(opts.saveCode);
    const [saveParams, setSaveParams] = useState(opts.saveParams);
    const [saveDsp, setSaveDsp] = useState(opts.saveDsp);
    const [realtimeCompile, setRealtimeCompile] = useState(opts.realtimeCompile);
    const [popup, setPopup] = useState(opts.popup);

    const handleVoicesChange = useCallback((v: string) => {
        setVoices(v);
        setOption("voices", Number(v));
    }, [setOption]);

    const handleBufferSizeChange = useCallback((v: string) => {
        setBufferSize(v);
        setOption("bufferSize", Number(v) as any);
    }, [setOption]);

    return (
        <div className="flex flex-col gap-2.5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Compile Options
            </span>

            {/* Poly Voices */}
            <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-500">Poly Voices</Label>
                <Select value={voices} onValueChange={handleVoicesChange}>
                    <SelectTrigger className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="0">Mono</SelectItem>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="4">4</SelectItem>
                        <SelectItem value="8">8</SelectItem>
                        <SelectItem value="16">16</SelectItem>
                        <SelectItem value="32">32</SelectItem>
                        <SelectItem value="64">64</SelectItem>
                        <SelectItem value="128">128</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Buffer Size */}
            <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-500">Buffer Size</Label>
                <Select
                    value={bufferSize}
                    onValueChange={handleBufferSizeChange}
                    disabled={getOptions().useWorklet}
                >
                    <SelectTrigger className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {getOptions().useWorklet && <SelectItem value="128">128</SelectItem>}
                        <SelectItem value="256">256</SelectItem>
                        <SelectItem value="512">512</SelectItem>
                        <SelectItem value="1024">1024</SelectItem>
                        <SelectItem value="2048">2048</SelectItem>
                        <SelectItem value="4096">4096</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Checkboxes */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="double"
                        checked={useDouble}
                        onCheckedChange={(v) => {
                            setUseDouble(!!v);
                            setOption("useDouble", !!v);
                        }}
                        className="h-3.5 w-3.5"
                    />
                    <Label htmlFor="double" className="text-xs text-zinc-400 cursor-pointer">
                        Double Precision
                    </Label>
                </div>

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="save-code"
                        checked={saveCode}
                        onCheckedChange={(v) => {
                            setSaveCode(!!v);
                            setOption("saveCode", !!v);
                        }}
                        className="h-3.5 w-3.5"
                    />
                    <Label htmlFor="save-code" className="text-xs text-zinc-400 cursor-pointer">
                        Save DSP Code
                    </Label>
                </div>

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="save-params"
                        checked={saveParams}
                        onCheckedChange={(v) => {
                            setSaveParams(!!v);
                            setOption("saveParams", !!v);
                        }}
                        className="h-3.5 w-3.5"
                    />
                    <Label htmlFor="save-params" className="text-xs text-zinc-400 cursor-pointer">
                        Save Params State
                    </Label>
                </div>

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="save-dsp"
                        checked={saveDsp}
                        onCheckedChange={(v) => {
                            setSaveDsp(!!v);
                            setOption("saveDsp", !!v);
                        }}
                        className="h-3.5 w-3.5"
                    />
                    <Label htmlFor="save-dsp" className="text-xs text-zinc-400 cursor-pointer">
                        Save DSP Cache
                    </Label>
                </div>

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="realtime"
                        checked={realtimeCompile}
                        onCheckedChange={(v) => {
                            setRealtimeCompile(!!v);
                            setOption("realtimeCompile", !!v);
                        }}
                        className="h-3.5 w-3.5"
                    />
                    <Label htmlFor="realtime" className="text-xs text-zinc-400 cursor-pointer">
                        Real-time Compile
                    </Label>
                </div>

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="popup"
                        checked={popup}
                        onCheckedChange={(v) => {
                            setPopup(!!v);
                            setOption("popup", !!v);
                        }}
                        className="h-3.5 w-3.5"
                    />
                    <Label htmlFor="popup" className="text-xs text-zinc-400 cursor-pointer">
                        Popup UI
                    </Label>
                </div>
            </div>
        </div>
    );
}
